// pages/FormViewerPage/hooks/useFormViewer/index.ts — useFormViewer orchestrator
import { useFormViewerState } from './state';
import { useFormViewerComputed } from './computed';
import { useFormViewerCoreEffects } from './effectsCore';
import { useFormViewerPixelEffects } from './effectsPixels';
import { useFormViewerFunnelEffects } from './effectsFunnel';
import { useFormViewerCartEffects } from './effectsCart';
import { useFormViewerTimerEffects } from './effectsTimers';
import { useFormViewerEverproEffects } from './effectsEverpro';
import { useFormViewerFormEffects } from './effectsForm';
import { useFormViewerSyncEffects } from './effectsSync';
import { useFormViewerValidate } from './actionsValidate';
import { useFormViewerSubmit } from './actionsSubmit';
import { useFormViewerCoupon } from './actionsCoupon';
import { useBump } from './bump';
import { useLoadPhase } from '../../../../utils/stagedLoad';
import { getFlatRegionKey } from '../../services/formViewerService';

export function useFormViewer(identifier: string, isSimpleEmbed: boolean = false, isSeamlessEmbed: boolean = false) {
    const s = useFormViewerState(identifier);
    const c = useFormViewerComputed(s, isSimpleEmbed);
    const coupon = useFormViewerCoupon(s, c);
    const bump = useBump(s.form);
    // Staged load: fase 2 = konten tampil + idle (pixel, ongkir);
    // fase 3 = analitik. Efek sekunder gate via phase (bukan serentak).
    const phase = useLoadPhase(!!s.form && !s.loading);

    useFormViewerCoreEffects(s);
    useFormViewerEverproEffects(s, phase);
    useFormViewerPixelEffects(s, phase);
    useFormViewerFunnelEffects(s);
    useFormViewerCartEffects(s);
    useFormViewerTimerEffects(s, c);
    useFormViewerFormEffects(s);
    useFormViewerSyncEffects(s, c, isSimpleEmbed);

    const a = useFormViewerValidate(s, c.displayOptions, isSimpleEmbed);
    const submit = useFormViewerSubmit(s, c, a.validateFields, isSimpleEmbed, isSeamlessEmbed, coupon.discountAmount, bump);

    return {
        form: s.form, loading: s.loading, notFound: s.notFound, formInactive: s.formInactive,
        isSubmitting: s.isSubmitting, submission: s.submission, setSubmission: s.setSubmission,
        imageLoaded: s.imageLoaded, setImageLoaded: s.setImageLoaded,
        timeLeft: s.timeLeft, variantStock: s.variantStock, checkoutCount: s.checkoutCount,
        messageTemplates: s.messageTemplates,
        customerData: s.customerData, setCustomerData: s.setCustomerData,
        fieldErrors: s.fieldErrors, setFieldErrors: s.setFieldErrors,
        addressData: s.addressData, setAddressData: s.setAddressData,
        selectedOptions: s.selectedOptions, setSelectedOptions: s.setSelectedOptions,
        selectedShippingKey: s.selectedShippingKey, setSelectedShippingKey: s.setSelectedShippingKey,
        selectedPaymentKey: s.selectedPaymentKey, setSelectedPaymentKey: s.setSelectedPaymentKey,
        selectedDuitkuMethod: s.selectedDuitkuMethod, setSelectedDuitkuMethod: s.setSelectedDuitkuMethod,
        selectedEverproRate: s.selectedEverproRate, setSelectedEverproRate: s.setSelectedEverproRate,
        selectedBiteshipRate: s.selectedBiteshipRate, setSelectedBiteshipRate: s.setSelectedBiteshipRate,
        biteshipApiKey: s.biteshipApiKey, brandEverpro: s.brandEverpro, brandMengantar: s.brandMengantar, brandLincah: (s as any).brandLincah, setBrandLincah: (s as any).setBrandLincah, forceShowPostalCode: s.forceShowPostalCode, setForceShowPostalCode: s.setForceShowPostalCode,
        formPagePixelConfigs: s.formPagePixelConfigs, thankYouPagePixelConfigs: s.thankYouPagePixelConfigs,
        formPagePixels: s.formPagePixels, thankYouPagePixels: s.thankYouPagePixels,
        activePixelIds: s.activePixelIds, eventNames: s.eventNames, pixelsByPlatform: s.pixelsByPlatform,
        activePlatform: s.activePlatform, globalTrackingSettings: s.globalTrackingSettings,
        settingsLoading: s.settingsLoading,
        displayOptions: c.displayOptions, compositeFallback: c.compositeFallback,
        currentCombination: c.currentCombination, currentVariantStock: c.currentVariantStock,
        shippingCost: c.shippingCost, codFee: c.codFee, EXCLUDED_PROVINCES: c.EXCLUDED_PROVINCES,
        getFlatRegionKey,
        sortedPaymentKeys: c.sortedPaymentKeys, resolvedVariantCombinations: c.resolvedVariantCombinations,
        productOptionsOverride: s.productOptionsOverride, linkedProductName: s.linkedProductName,
        pixelsLoaded: s.pixelsLoaded, hasSubmittedRef: s.hasSubmittedRef, curComboRef: s.curComboRef, rateHoldRef: s.rateHoldRef, ratesSettledRef: s.ratesSettledRef,
        lowPerfNoCod: s.lowPerfNoCod, setLowPerfNoCod: s.setLowPerfNoCod,
        handleSubmit: submit.handleSubmit, applyOrderUpdates: s.applyOrderUpdates,
        validateCustomerFields: a.validateFields,
        herbalClaimMode: s.herbalClaimMode, setHerbalClaimMode: s.setHerbalClaimMode,
        couponCode: s.couponCode, setCouponCode: s.setCouponCode,
        couponApplied: coupon.couponApplied, couponError: coupon.couponError, couponApplying: coupon.couponApplying,
        discountAmount: coupon.discountAmount, applyCoupon: coupon.applyCoupon, clearCoupon: coupon.clearCoupon,
        quantity: s.quantity, setQuantity: s.setQuantity,
        bumpOptions: bump.bumpOptions, selectedBumpIds: bump.selectedBumpIds, toggleBump: bump.toggleBump,
        bumpSum: bump.bumpSum, bumpDiscount: bump.bumpDiscount, bumpWeight: bump.bumpWeight,
    };
}
